import { FieldValidator, FormApi } from 'final-form'
import _ from 'lodash'
import { FC, useCallback, useEffect, useState } from 'react'
import { Form, FormSpy, useFormState } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

import { Box } from '../../../components/Box'
import { Button } from '../../../components/Button'
import { CenterChildren } from '../../../components/Center'
import { CollapseContent } from '../../../components/Collapse'
import { Flex } from '../../../components/Flex'
import { Icon } from '../../../components/Icons'
import { RadioPanel } from '../../../components/RadioPanel'
import { Space } from '../../../components/Space'
import { Spin, SpinSize } from '../../../components/Spin'
import { Text, Title } from '../../../components/Text'
import { useDateFormat } from '../../../hooks/useDateFormat'
import { useViewportSize } from '../../../hooks/useViewportSize'
import { DeliveryOptionLabelRenderer } from '../../../pages/utils/config'
import { Company } from '../../../redux/companyDetail/types'
import { getCmsLoginUrl, getPreferredProductsEnabled, isPreferredProductAllowedCountry } from '../../../redux/config'
import { DialogActions } from '../../../redux/dialog'
import { DomainData, DomainType } from '../../../redux/domains'
import { getDomain, getDomainsLoading } from '../../../redux/domains/selectors'
import { NotificationType } from '../../../redux/account/notifications'
import {
  AddToCartRequestBody,
  getNumberOfOrderItems,
  getOrderId,
  isAddingToCartOrBuyingNow,
  isReportAddingToCart,
  isReportBuyingNow,
  OrderActions
} from '../../../redux/order'
import { RootState } from '../../../redux/types'
import { isUserLoggedIn } from '../../../redux/user/selectors'
import { useReduxEvent } from '../../../utils/eventr'
import { createTFunction, getCompanyCountry } from '../../../utils/helpers'
import { formatPath } from '../../../utils/routes'
import { createFormValidator, error, requiredIf } from '../../../utils/validation'
import { SelectField } from '../../FormFields/SelectField'
import { DropDownPickerField } from '../../../containers/FormFields'
import { useAuthorizations } from '../../../hooks/useAuthorizations'
import { useApi, usePrevious } from '../../../hooks'
import { Tooltip } from '../../../components/Tooltip'
import { useBriqTheme } from '../../../hooks/useBriqTheme'
import { getVisibleDialog } from '../../../redux/dialog/selectors'
import { ConsentResponse } from '../../../hooks/useConsentData'
import { PriceWrapper } from '../../PriceWrapper'
import { FieldSpace } from '../../../components/FieldSpace'
import { useCountries } from '../../../hooks/useCountries'

import { ArrowIcon, ReportContainer, ReportActionsContainer, ReportActions } from './styled'
import { Report, ReportFormValues, ReportType } from './types'

export type ReportUmbracoData = {
  bifCode: string
  childProductBifCode: string
  alert: string
  additionalInfo: string
  name: string
  previewDescription: string
  sampleUrl: string
  productDetailLinkLabel: string
  downloadSampleLabel: string
}

const Alert = styled.div`
  display: flex;
  align-items: center;
`

const AlertIcon = styled(Icon)`
  font-size: 20px;
  margin-right: 8px;
`

const PreferredSettingSpace = styled(Space)`
  svg {
    cursor: pointer;
  }
`

interface ReportPropertiesProps {
  filteredLanguages: DomainData[]
  filteredDeliveryOptions: DomainData[]
  report: Report
  umbracoData?: ReportUmbracoData
  deliveryOption: string
  language: string
  languageFormName: string
  deliveryOptionFormName: string
  documentDateFormName: string
  isSelected: boolean
  onSelectReportType: () => void
  displayAsPanel: boolean
  form: FormApi<ReportFormValues>
  renderDeliveryOptionLabel: DeliveryOptionLabelRenderer
  showPriceForAnonymousUser?: boolean
  proofsOfInterest?: string[]
  proofOfInterestOptionsFormName: string
}

const ReportProperties: FC<ReportPropertiesProps> = ({
  filteredLanguages,
  filteredDeliveryOptions,
  report,
  umbracoData,
  deliveryOption,
  renderDeliveryOptionLabel,
  language,
  languageFormName,
  deliveryOptionFormName,
  documentDateFormName,
  isSelected,
  onSelectReportType,
  displayAsPanel,
  form,
  showPriceForAnonymousUser = true,
  proofsOfInterest,
  proofOfInterestOptionsFormName
}) => {
  const { formatDate } = useDateFormat()
  const { t } = useTranslation(['core', 'form', 'companyDetail', 'investigation'])
  const domainsLoading = useSelector(getDomainsLoading)
  const formState = useFormState()
  const viewPort = useViewportSize()

  const tf = createTFunction('form:', t)

  useReduxEvent(event => {
    const { productCode } = event.data
    if (report.code == productCode) {
      form.change('acceptedConsent', productCode)
      form.submit()
    }
  }, NotificationType.ConsentAccepted)

  useEffect(() => {
    // Select this report type if user selected language || deliveryOption
    if (formState.dirtyFields[deliveryOptionFormName] || formState.dirtyFields[languageFormName]) {
      onSelectReportType()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, deliveryOption])

  let priceOption = undefined
  if (report.type === ReportType.Offline) {
    const selectedDeliveryOption = deliveryOption
      ? _.find(report.deliveryOptions, x => x.code === deliveryOption)
      : undefined

    priceOption = selectedDeliveryOption
  } else {
    priceOption = _.head(report.deliveryOptions)
  }

  const displayLastUpdateDate = report.type === ReportType.Online && report.lastUpdateDate
  const displayDeliveryOption = report.type === ReportType.Offline
  const deliveryOptionWidth = _.size(proofsOfInterest) > 0 ? 0.4 : 0.5

  const reportProperties = (
    <Flex flex={1} flexDirection="column">
      <Flex flex={1} mx={-2} flexWrap="wrap">
        {!displayAsPanel && (
          <Box width={1} px={2}>
            {umbracoData?.additionalInfo}
          </Box>
        )}

        {!_.isEmpty(report.documentDates) && (
          <Box width={[1, 1 / 2, 1 / 3]} px={2} py={[1, 1, 2]}>
            <FieldSpace>
              <Text block bold color="primary" display="flex" alignItems="center">
                {tf('DocumentDate')}:
              </Text>
              <Box flex="1 1 auto" display="flex">
                <DropDownPickerField
                  name={documentDateFormName}
                  additionalProps={{
                    dataSet: report.documentDates,
                    buttonText: tf('DocumentDatePlaceholder'),
                    renderMenuItemLabel: (key: string) =>
                      !_.isEmpty(report.documentDates)
                        ? formatDate(report.documentDates[key], { dateStyle: 'medium' })
                        : '',
                    center: false
                  }}
                />
              </Box>
            </FieldSpace>
          </Box>
        )}

        {_.isEmpty(report.documentDates) && (
          <>
            <Box width={[1, 1 / 2, 1 / 4]} px={2} py={[1, 1, 2]}>
              <FieldSpace>
                <Text block bold color="primary" display="flex" alignItems="center">
                  {tf('Language')}:
                </Text>
                <Box flex="1 1 auto" display="flex">
                  <SelectField
                    name={languageFormName}
                    options={_.map(filteredLanguages, x => ({
                      label: x.description,
                      value: x.code
                    }))}
                    placeholder={tf('LanguagePlaceholder')}
                    additionalProps={{
                      $colorVariant: 'primary',
                      loading: domainsLoading[DomainType.Culture],
                      className: 'primaryColorVariantSelect'
                    }}
                  />
                </Box>
              </FieldSpace>
            </Box>

            {_.size(proofsOfInterest) > 0 && (
              <Box width={[1, 1 / 2, 0.35]} px={2} py={[1, 1, 2]}>
                <FieldSpace>
                  <Text block bold color="primary">
                    {tf('ProofOfInterest')}:
                  </Text>
                  <Box width="100%" maxWidth={['auto', '300px']} alignItems="center" display={['flex', 'flex', null]}>
                    <SelectField
                      name={proofOfInterestOptionsFormName}
                      options={_.map(
                        _.sortBy(proofsOfInterest, p => p, ['asc']),
                        proofOfInterest => ({ value: proofOfInterest, label: proofOfInterest })
                      )}
                      placeholder={tf('ProofOfInterestPlaceholder')}
                      additionalProps={{ $colorVariant: 'primary', className: 'primaryColorVariantSelect' }}
                    />
                  </Box>
                </FieldSpace>
              </Box>
            )}

            {((!viewPort?.isMobile && _.size(proofsOfInterest) == 0) ||
              displayLastUpdateDate ||
              displayDeliveryOption) && (
              <Box width={[1, 1 / 2, deliveryOptionWidth]} px={2} py={[1, 1, 2]}>
                {displayLastUpdateDate && report.lastUpdateDate && (
                  <FieldSpace>
                    <Text block bold color="primary" display="flex" alignItems="center">
                      {tf('LastUpdate')}:
                    </Text>
                    <Text>{formatDate(new Date(report.lastUpdateDate), { dateStyle: 'medium' })}</Text>
                  </FieldSpace>
                )}
                {displayDeliveryOption && (
                  <FieldSpace>
                    <Text block bold color="primary">
                      {tf('DeliveryOption')}:
                    </Text>
                    <Box width="100%" maxWidth={['auto', '300px']} alignItems="center" display={['flex', 'flex', null]}>
                      <SelectField
                        name={deliveryOptionFormName}
                        options={_.map(filteredDeliveryOptions, x => {
                          const d = _.find(report.deliveryOptions, y => y.code === x.code)

                          return {
                            label: renderDeliveryOptionLabel(x, d, t),
                            value: x.code
                          }
                        })}
                        placeholder={tf('DeliveryOptionPlaceholder')}
                        additionalProps={{
                          $colorVariant: 'primary',
                          loading: domainsLoading[DomainType.DeliveryOption],
                          className: 'primaryColorVariantSelect'
                        }}
                      />
                    </Box>
                  </FieldSpace>
                )}
              </Box>
            )}
          </>
        )}
        <Flex width={[1, 1 / 4, 1 / 4]} px={2} py={[1, 1, 2]} alignItems={[null, null, 'center']} flexGrow={1}>
          {priceOption && (
            <PriceWrapper
              grossValue={priceOption.displayGrossValue}
              netValue={priceOption.displayNetValue}
              showPriceForAnonymousUser={showPriceForAnonymousUser}
            />
          )}
        </Flex>
      </Flex>
      {_.trim(umbracoData?.alert) && (
        <Alert>
          <AlertIcon color="warning" type="ExclamationCircleOutlined" />
          {umbracoData?.alert}
        </Alert>
      )}
    </Flex>
  )

  if (displayAsPanel) {
    return (
      <RadioPanel value={isSelected} onChange={onSelectReportType} header={umbracoData?.additionalInfo}>
        {reportProperties}
      </RadioPanel>
    )
  }

  return reportProperties
}

const requiredWhenParentReportTypeOffline = (): FieldValidator<any> => (value, values: any, meta) => {
  if (
    !_.isEmpty(meta?.data?.documentDates) ||
    meta?.data?.parentReportType === ReportType.Online ||
    values.selectedReportIndex !== 0
  )
    return undefined
  return _.isNil(value) ? error('Required') : undefined
}

const requiredWhenChildReportTypeOffline = (): FieldValidator<any> => (value, values: any, meta) => {
  if (
    !_.isEmpty(meta?.data?.documentDates) ||
    meta?.data?.childReportType === ReportType.Online ||
    values.selectedReportIndex !== 1
  )
    return undefined
  return _.isNil(value) ? error('Required') : undefined
}

const validator = createFormValidator<ReportFormValues>({
  parentLanguage: [
    requiredIf((values, meta) => _.isEmpty(meta?.data?.documentDates) && values.selectedReportIndex === 0)
  ],
  childLanguage: [
    requiredIf((values, meta) => _.isEmpty(meta?.data?.documentDates) && values.selectedReportIndex === 1)
  ],
  parentDeliveryOption: [requiredWhenParentReportTypeOffline()],
  childDeliveryOption: [requiredWhenChildReportTypeOffline()],
  documentVersion: [requiredIf((_1, meta) => !_.isEmpty(meta?.data?.documentDates))],
  parentProofOfInterest: [
    requiredIf((values, meta) => meta?.data?.parentProofOfInterestRequired && values.selectedReportIndex === 0)
  ],
  childProofOfInterest: [
    requiredIf((values, meta) => meta?.data?.childProofOfInterestRequired && values.selectedReportIndex === 1)
  ]
})

type Props = {
  report: Report
  umbracoData?: ReportUmbracoData
  childReport?: Report
  childUmbracoData?: ReportUmbracoData
  loading: boolean
  company?: Company
  requireConsent?: boolean
  consentData?: ConsentResponse
  renderDeliveryOptionLabel: DeliveryOptionLabelRenderer
  index: number
  isParentPreferred: boolean
  isChildPreferred: boolean
  isAnotherProductPreferred: boolean
  refreshPreferred: () => void
  loadingPreferred: boolean
  showPriceForAnonymousUser?: boolean
}

export const ReportComponent = ({
  report,
  umbracoData,
  childReport,
  childUmbracoData,
  loading,
  company,
  requireConsent,
  renderDeliveryOptionLabel,
  consentData,
  index,
  isParentPreferred,
  isChildPreferred,
  refreshPreferred,
  isAnotherProductPreferred,
  loadingPreferred,
  showPriceForAnonymousUser = true
}: Props) => {
  const viewPort = useViewportSize()
  const { t, i18n } = useTranslation(['core', 'form', 'companyDetail', 'investigation'])

  const tcr = createTFunction('companyDetail:Reports.', t)
  const tc = createTFunction('core:', t)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const countryCode = company && getCompanyCountry(company?.companyIdentifier)
  const { countries: countriesList } = useCountries()
  const countryDescription = company?.hqAddress.countryCode
    ? _.find(countriesList, country => country.code === company?.hqAddress.countryCode)?.label ??
      company?.hqAddress.countryCode
    : ''

  const languages = useSelector((state: RootState) =>
    getDomain(state, { domain: DomainType.Culture, language: i18n.language })
  )
  const deliveryOptions = useSelector((state: RootState) =>
    getDomain(state, { domain: DomainType.DeliveryOption, language: i18n.language })
  )

  const getFilteredLanguages = (rep: Report) => _.filter(languages, x => _.includes(rep.languages, x.code))
  const getDefaultReportLanguage = (rep?: Report) => {
    if (!rep) return undefined

    const filteredLanguages = getFilteredLanguages(rep)
    return _.size(filteredLanguages) === 1 ? filteredLanguages[0].code : undefined
  }

  const getFilteredDeliveryOptions = (rep: Report) =>
    _.filter(deliveryOptions, x => !!_.find(rep.deliveryOptions, y => x.code === y.code))

  const loggedIn = useSelector(isUserLoggedIn)
  const loginUrl = useSelector(getCmsLoginUrl)
  const orderId = useSelector(getOrderId)
  const cartItemsCount = useSelector(getNumberOfOrderItems)

  const isGroupedDisplay = Boolean(report && childReport)
  const parentDeliveryOptions = getFilteredDeliveryOptions(report)
  const childDeliveryOptions = childReport && getFilteredDeliveryOptions(childReport)

  const displayUmbracoData = childReport?.type === ReportType.Offline ? childUmbracoData : umbracoData

  const cartItemSubmitting = useSelector(isAddingToCartOrBuyingNow)
  const isAddingToCart = useSelector((state: RootState) =>
    isReportAddingToCart(state, { companyId: company?.id || '', reportCode: report?.code || '' })
  )
  const isBuyingNow = useSelector((state: RootState) =>
    isReportBuyingNow(state, { companyId: company?.id || '', reportCode: report?.code || '' })
  )

  const { isAuthorizedForOrdering } = useAuthorizations()

  const isPreferred = isParentPreferred || isChildPreferred

  const preferredProductsEnabled = useSelector(getPreferredProductsEnabled)
  const preferredProductAllowedCountry = useSelector((state: RootState) =>
    isPreferredProductAllowedCountry(state, countryCode)
  )

  const { request, loading: changingPreferredProduct } = useApi()

  const [selectedReportIndex, setSelectedReportIndex] = useState<number | undefined>()

  const theme = useBriqTheme()

  const [preferredProductChangeApprovalState, setPreferredProductChangeApprovalState] = useState<
    'Initial' | 'WarningDisplayed' | 'Approved'
  >('Initial')

  const triggerPreferredProductChange = useCallback(async () => {
    try {
      await request({
        url: '/api/preferredProducts',
        method: isPreferred ? 'delete' : 'put',
        data: {
          countryCode,
          productCode: isPreferred
            ? // DELETE case
              isParentPreferred
              ? report.code
              : childReport?.code
            : // UPDATE case
              !selectedReportIndex
              ? report.code
              : childReport?.code
        }
      })
    } catch {
      dispatch(
        DialogActions.showInfoDialog({
          title: tc('Error'),
          text: tcr('ErrorChangingPreferredProduct')
        })
      )
    } finally {
      refreshPreferred()
    }
  }, [
    childReport?.code,
    countryCode,
    dispatch,
    isParentPreferred,
    isPreferred,
    refreshPreferred,
    report.code,
    request,
    selectedReportIndex,
    tc,
    tcr
  ])

  const visibleDialog = useSelector(getVisibleDialog)
  const previousVisibleDialog = usePrevious(visibleDialog)
  useEffect(() => {
    if (
      previousVisibleDialog === 'ConfirmPreferredProductChangeDialog' &&
      visibleDialog !== previousVisibleDialog &&
      _.includes(['WarningDisplayed', 'Approved'], preferredProductChangeApprovalState)
    ) {
      if (preferredProductChangeApprovalState === 'Approved') {
        triggerPreferredProductChange()
      }
      setPreferredProductChangeApprovalState('Initial')
    }
  }, [preferredProductChangeApprovalState, previousVisibleDialog, triggerPreferredProductChange, visibleDialog])

  return countryCode ? (
    <ReportContainer data-automation-id="reportContainer" isPreferred={isPreferred}>
      <Title level="medium">
        <Flex justifyContent="space-between">
          <Flex alignItems="center">
            <Icon type={_.isEmpty(report.documentDates) ? 'CaseBlue' : 'FileTextOutlined'} />
            <span className="report-name" data-automation-id="reportName">
              {loading && <Spin size={SpinSize.Default} delay={300} />}
              {!loading &&
                (childReport?.type === ReportType.Offline
                  ? childUmbracoData?.name || childReport.bifName
                  : umbracoData?.name || report.bifName)}
            </span>
          </Flex>
          {preferredProductsEnabled &&
            loggedIn &&
            _.isEmpty(report.documentDates) &&
            preferredProductAllowedCountry && (
              <PreferredSettingSpace alignItems="center">
                <Tooltip
                  placement="left"
                  trigger={['hover', 'click']}
                  title={tcr('PreferredProductPopupInfo')}
                  color="white"
                  overlayInnerStyle={{ color: theme?.colors?.textPrimary }}
                >
                  <Icon type="Info" />
                </Tooltip>
                {changingPreferredProduct || loadingPreferred ? (
                  <Spin size={SpinSize.Small} delay={0} />
                ) : (
                  <Icon
                    type={isPreferred ? 'StarFull' : 'Star'}
                    onClick={async () => {
                      if (
                        !isPreferred &&
                        isAnotherProductPreferred &&
                        preferredProductChangeApprovalState !== 'Approved'
                      ) {
                        dispatch(
                          DialogActions.showDialogWithData({
                            dialog: 'ConfirmPreferredProductChangeDialog',
                            data: {
                              allowPreferredProductChange: () => setPreferredProductChangeApprovalState('Approved')
                            }
                          })
                        )
                        setPreferredProductChangeApprovalState('WarningDisplayed')
                        return
                      }
                      await triggerPreferredProductChange()
                    }}
                  />
                )}
              </PreferredSettingSpace>
            )}
        </Flex>
      </Title>
      <Box pt="12px" display={['none', 'none', 'block']}>
        <span
          className="desc"
          dangerouslySetInnerHTML={{
            __html: displayUmbracoData?.previewDescription || ''
          }}
        ></span>
      </Box>
      {loading ? (
        <CenterChildren>
          <Spin delay={300} />
        </CenterChildren>
      ) : (
        <Form<ReportFormValues>
          initialValues={{
            parentLanguage: getDefaultReportLanguage(report),
            childLanguage: getDefaultReportLanguage(childReport),
            parentDeliveryOption: _.size(parentDeliveryOptions) === 1 ? _.head(parentDeliveryOptions)?.code : undefined,
            childDeliveryOption: _.size(childDeliveryOptions) === 1 ? _.head(childDeliveryOptions)?.code : undefined,
            selectedReportIndex: isChildPreferred ? 1 : 0
          }}
          validate={validator({
            parentReportType: report.type,
            childReportType: childReport?.type,
            documentDates: report.documentDates,
            parentProofOfInterestRequired: _.size(report.proofsOfInterest) > 0,
            childProofOfInterestRequired: _.size(childReport?.proofsOfInterest) > 0
          })}
          onSubmit={async values => {
            if (!isAuthorizedForOrdering) {
              dispatch(
                DialogActions.showInfoDialog({
                  text: tcr('BRIQReadOnlyPermissionMsgText')
                })
              )
              return
            }

            const errors = validator({
              parentReportType: report.type,
              childReportType: childReport?.type,
              documentDates: report.documentDates,
              parentProofOfInterestRequired: _.size(report.proofsOfInterest) > 0,
              childProofOfInterestRequired: _.size(childReport?.proofsOfInterest) > 0
            })(values)
            if (errors) return errors
            if (!company) return undefined

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const selectedReport = values.selectedReportIndex === 0 ? report : childReport!

            const productCode = values.selectedReportIndex === 0 ? report.code : childReport?.code || ''

            const deliveryOptionCode =
              selectedReport.type === ReportType.Offline
                ? values.selectedReportIndex === 0
                  ? values.parentDeliveryOption
                  : values.childDeliveryOption
                : undefined

            const deliveryOption = _.find(report.deliveryOptions, x => x.code == deliveryOptionCode)

            const price = deliveryOption?.grossValue
            const currency = deliveryOption?.currency

            const formData: AddToCartRequestBody = {
              orderId,
              customerRequests:
                values.documentVersion === undefined
                  ? [
                      {
                        lastUpdateDate:
                          values.selectedReportIndex === 0 ? report.lastUpdateDate : childReport?.lastUpdateDate,
                        entityName: company.name || '',
                        entityIdentifierEncoded: company.id,
                        countryCode: countryCode,
                        deliveryOptionCode,
                        languageCode: values.selectedReportIndex === 0 ? values.parentLanguage : values.childLanguage,
                        productCode,
                        consentProvided: values.acceptedConsent === productCode,
                        materialTypePriceParam: report.materialTypePriceParam,
                        countryCodePriceParam: report.countryCodePriceParam,
                        isFinancialPriceParam: report.isFinancialPriceParam,
                        inRevisionPriceParam: report.inRevisionPriceParam,
                        dataTypePriceParam: report.dataTypePriceParam,
                        providerCode: report.providerCode,
                        providerName: report.providerName,
                        proofOfInterest:
                          values.selectedReportIndex === 0 ? values.parentProofOfInterest : values.childProofOfInterest,
                        investigationData:
                          _.startsWith(productCode, 'SKYCREDITINV_') || _.startsWith(productCode, 'SKYKYCINV_')
                            ? {
                                companyName: company.name || '',
                                countryCode: company?.hqAddress.countryCode,
                                addressLine: company?.hqAddress.fullAddress || countryDescription,
                                registrationNumbers: []
                              }
                            : undefined
                      }
                    ]
                  : _.map(values.documentVersion, versionId => {
                      return {
                        lastUpdateDate:
                          values.selectedReportIndex === 0 ? report.lastUpdateDate : childReport?.lastUpdateDate,
                        entityName: company.name || '',
                        entityIdentifierEncoded: company.id,
                        countryCode: countryCode,
                        deliveryOptionCode,
                        languageCode: values.selectedReportIndex === 0 ? values.parentLanguage : values.childLanguage,
                        productCode,
                        consentProvided: values.acceptedConsent === productCode,
                        materialTypePriceParam: report.materialTypePriceParam,
                        countryCodePriceParam: report.countryCodePriceParam,
                        isFinancialPriceParam: report.isFinancialPriceParam,
                        inRevisionPriceParam: report.inRevisionPriceParam,
                        dataTypePriceParam: report.dataTypePriceParam,
                        documentDate:
                          !_.isEmpty(report.documentDates) && values.documentVersion
                            ? moment(report.documentDates[versionId]).format('YYYY-MM-DD')
                            : undefined,
                        versionId: versionId
                      }
                    })
            }

            if (requireConsent && values.acceptedConsent !== productCode) {
              if (
                !_.isEmpty(consentData?.okButtonText) &&
                !_.isEmpty(consentData?.content) &&
                !_.isEmpty(consentData?.title)
              ) {
                dispatch(
                  DialogActions.showCompanyConsentDialog({
                    productCode,
                    content: consentData?.content || '',
                    okButtonText: consentData?.okButtonText || '',
                    title: consentData?.title || '',
                    cancelButtonText: consentData?.cancelButtonText
                  })
                )
                return undefined
              }
            }

            if (values.mode === 'AddToCart') {
              dispatch(
                OrderActions.addToCart({
                  companyId: company.id,
                  reportCode: report.code,
                  formData,
                  gtmData: {
                    button: index * 2 - 1,
                    cartItemsCount,
                    company,
                    currency,
                    value: price,
                    price,
                    reportName: report.orderingName
                  },
                  navigate
                })
              )
            } else if (values.mode === 'BuyNow') {
              dispatch(
                OrderActions.buyNow({
                  companyId: company.id,
                  reportCode: report.code,
                  formData,
                  gtmData: {
                    button: index * 2,
                    cartItemsCount,
                    company,
                    currency,
                    value: price,
                    price,
                    reportName: report.orderingName
                  },
                  navigate
                })
              )
            }
          }}
          render={({ handleSubmit, values, form }) => {
            return (
              <form onSubmit={handleSubmit}>
                <FormSpy<ReportFormValues>
                  subscription={{ values: true }}
                  onChange={({ values }) =>
                    _.defer(() => {
                      // use defer due to likely bug in react-final-form https://github.com/final-form/react-final-form/issues/809
                      setSelectedReportIndex(values?.selectedReportIndex)
                    })
                  }
                />
                <ReportProperties
                  filteredDeliveryOptions={parentDeliveryOptions}
                  filteredLanguages={getFilteredLanguages(report)}
                  report={report}
                  umbracoData={umbracoData}
                  deliveryOption={values.parentDeliveryOption}
                  language={values.parentLanguage}
                  languageFormName="parentLanguage"
                  deliveryOptionFormName="parentDeliveryOption"
                  documentDateFormName="documentVersion"
                  isSelected={values.selectedReportIndex === 0}
                  onSelectReportType={() => form.change('selectedReportIndex', 0)}
                  displayAsPanel={isGroupedDisplay}
                  form={form}
                  renderDeliveryOptionLabel={renderDeliveryOptionLabel}
                  showPriceForAnonymousUser={showPriceForAnonymousUser}
                  proofsOfInterest={report.proofsOfInterest}
                  proofOfInterestOptionsFormName="parentProofOfInterest"
                />

                {childReport && (
                  <ReportProperties
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    filteredDeliveryOptions={childDeliveryOptions!}
                    filteredLanguages={getFilteredLanguages(childReport)}
                    report={childReport}
                    umbracoData={childUmbracoData}
                    deliveryOption={values.childDeliveryOption}
                    language={values.childLanguage}
                    languageFormName="childLanguage"
                    deliveryOptionFormName="childDeliveryOption"
                    documentDateFormName="documentDate"
                    isSelected={values.selectedReportIndex === 1}
                    onSelectReportType={() => form.change('selectedReportIndex', 1)}
                    displayAsPanel={isGroupedDisplay}
                    form={form}
                    renderDeliveryOptionLabel={renderDeliveryOptionLabel}
                    showPriceForAnonymousUser={showPriceForAnonymousUser}
                    proofsOfInterest={childReport.proofsOfInterest}
                    proofOfInterestOptionsFormName="childProofOfInterest"
                  />
                )}

                <ReportActionsContainer
                  borderTop={viewPort?.isDesktop || viewPort?.isWide}
                  borderBottom={viewPort?.isMobile || viewPort?.isTablet}
                  data-automation-id="reportActionsContainer"
                >
                  <ReportActions hasSample={!!displayUmbracoData?.sampleUrl}>
                    {displayUmbracoData?.sampleUrl && (
                      <a href={encodeURI(formatPath(displayUmbracoData?.sampleUrl))} target="blank">
                        <Button
                          variant="transparent"
                          icon={<Icon type="DownloadOutlined" />}
                          padding="3px 20px 3px 0"
                          data-automation-id="btnDownloadSample"
                        >
                          {displayUmbracoData?.downloadSampleLabel || t('Product.DownloadSample')}
                        </Button>
                      </a>
                    )}

                    <Space space={2} alignItems="center" className="buttons">
                      {!loggedIn && (
                        <>
                          <Text>{tcr('LogInPrompt')}</Text>
                          <a
                            href={formatPath(loginUrl ? encodeURI(loginUrl) : undefined, {
                              returnUrl: window.location.href
                            })}
                          >
                            <Button
                              className="qa--report-login-button"
                              variant="primary"
                              htmlType="button"
                              data-automation-id="btnLogin"
                            >
                              {tc('Login')}
                            </Button>
                          </a>
                        </>
                      )}
                      {loggedIn && (
                        <>
                          <Button
                            variant="primary"
                            loading={isAddingToCart}
                            disabled={cartItemSubmitting}
                            onClick={() => {
                              form.change('mode', 'AddToCart')
                            }}
                            htmlType="submit"
                            data-automation-id="btnAddToCart"
                          >
                            <Icon type="ShoppingCartOutlined" color="white" />
                          </Button>
                          <Button
                            className="qa--buy-now-button"
                            variant="secondary"
                            htmlType="submit"
                            loading={isBuyingNow}
                            disabled={cartItemSubmitting}
                            onClick={() => {
                              form.change('mode', 'BuyNow')
                            }}
                            data-automation-id="btnBuyNow"
                          >
                            {t('BuyNow')}
                          </Button>
                        </>
                      )}
                    </Space>
                  </ReportActions>
                </ReportActionsContainer>
                {(viewPort?.isMobile || viewPort?.isTablet) && (
                  <Flex mx={-2} flexWrap="wrap">
                    <Box width={1}>
                      <CollapseContent
                        renderHeader={open => (
                          <Text color="primary" bold block justifyContent="center" display="flex">
                            {t('form:Description')}
                            <ArrowIcon opened={open} type="arrowRightBlue" width="24px" height="24px" />
                          </Text>
                        )}
                      >
                        <Text
                          block
                          dangerouslySetInnerHTML={{
                            __html: displayUmbracoData?.previewDescription || ''
                          }}
                          px={2}
                        />
                      </CollapseContent>
                    </Box>
                  </Flex>
                )}
              </form>
            )
          }}
        />
      )}
    </ReportContainer>
  ) : null
}
